import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { brokerInfo, brokerShortName, refreshBrokerInfo, useBrokerName } from "./broker";
import Dashboard from "./pages/Dashboard";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";

/* Only the two screens that can be the first paint — Login and Dashboard — ship
 * in the main bundle. Every other route is a separate chunk fetched on demand,
 * which keeps ~340 kB of parsed JS (Manual Trader, Data Collection,
 * the labs, Settings) out of the landing page. The chunks are warmed on idle
 * below, so navigation still feels instant. */
const DataCollection = lazy(() => import("./pages/DataCollection"));
const AutoTrader = lazy(() => import("./pages/AutoTrader"));
const ManualTrader = lazy(() => import("./pages/ManualTrader"));
const RulesLab = lazy(() => import("./pages/RulesLab"));
const MLModels = lazy(() => import("./pages/MLModels"));
const Backtests = lazy(() => import("./pages/Backtests"));
const RuleBuilder = lazy(() => import("./pages/RuleBuilder"));
const RuleTutorial = lazy(() => import("./pages/RuleTutorial"));
const Settings = lazy(() => import("./pages/Settings"));
const AlgoAI = lazy(() => import("./pages/AlgoAI"));
const GarchRange = lazy(() => import("./pages/GarchRange"));
const GarchFences = lazy(() => import("./pages/GarchFences"));
const Decide = lazy(() => import("./pages/Decide"));
const StructureRegime = lazy(() => import("./pages/StructureRegime"));
const OIFib = lazy(() => import("./pages/OIFib"));
const EquityEdge = lazy(() => import("./pages/EquityEdge"));

// Warm every route chunk once the landing screen is idle, so the first click on
// a nav item does not pay the download.
const PREFETCH = [
  () => import("./pages/Decide"),
  () => import("./pages/AutoTrader"),
  () => import("./pages/ManualTrader"),
  () => import("./pages/RulesLab"),
  () => import("./pages/RuleBuilder"),
  () => import("./pages/RuleTutorial"),
  () => import("./pages/MLModels"),
  () => import("./pages/Backtests"),
  () => import("./pages/Settings"),
  () => import("./pages/DataCollection"),
  () => import("./pages/GarchRange"),
  () => import("./pages/GarchFences"),
  () => import("./pages/StructureRegime"),
  () => import("./pages/OIFib"),
  () => import("./pages/EquityEdge"),
];

function usePrefetchRoutes() {
  useEffect(() => {
    let cancelled = false;
    // Bound to window: an unbound WebIDL method can throw "Illegal invocation"
    // once Babel puts this module in strict mode.
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback.bind(window)
      : (fn) => window.setTimeout(fn, 1500);
    const cancelIdle = window.cancelIdleCallback
      ? window.cancelIdleCallback.bind(window)
      : window.clearTimeout.bind(window);
    // One chunk per idle slot: never compete with the live SSE streams.
    let i = 0;
    let handle = idle(function next() {
      if (cancelled || i >= PREFETCH.length) return;
      PREFETCH[i++]().catch(() => {});
      handle = idle(next);
    });
    return () => { cancelled = true; cancelIdle(handle); };
  }, []);
}

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <div className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-eyebrow text-low">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
        Loading screen…
      </div>
    </div>
  );
}
import { ToastProvider, useToast } from "./components/ui/Toast";
import { ConfirmProvider } from "./components/ui/ConfirmDialog";
import { ThemeProvider } from "./context/ThemeContext";
import { TraderStatusContext } from "./context/TraderStatusContext";
import { Sidebar } from "./components/layout/Sidebar";
import { TopBar } from "./components/layout/TopBar";
import { CommandPalette } from "./components/layout/CommandPalette";
import { findNavItem } from "./components/layout/navigation";
import { ErrorBoundary } from "./components/layout/ErrorBoundary";
import {
  fetchAuthStatus, fetchLoginUrl, fetchBrokers, fetchBrokerFunds, startBrokerLogin, autoLoginBroker, getAutoTraderStatus,
  fetchAutotradePositions, appVerify,
} from "./api";
import { usePolling } from "./hooks/usePolling";

/* /autotrade/status filters open_trades to the CURRENT mode, so after a
 * LIVE→PAPER switch the header counted zero while live rows were still open.
 * /autotrade/positions lists both books (and the manual book) with the same
 * priced shape, so the shell polls the pair and hands every consumer the
 * union. The mode-only list is kept as `open_trades_mode` for reference. */
function mergeStatus(status, positions) {
  if (!status) return status;
  if (!Array.isArray(positions)) return { ...status, open_trades_all_books: false };
  return { ...status, open_trades: positions, open_trades_mode: status.open_trades, open_trades_all_books: true };
}

// "Kite" / "m.Stock" / "Dhan" for the header. `name` is /auth/status's broker_name, which always
// describes the CURRENT default; the /brokers row only lends its label when it is that same broker.
function headerBrokerLabel(name, row) {
  const code = name || row?.name;
  if (!code) return "Kite";                       // an older backend that names no broker
  const label = row && row.name === code ? (row.label || "").replace("Zerodha ", "") || undefined : undefined;
  return brokerShortName(code, label);
}

function AppContent() {
  const [auth, setAuth] = useState(null);
  const [defaultBroker, setDefaultBroker] = useState(null);
  const [traderStatus, setTraderStatus] = useState(null);
  const [funds, setFunds] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [pollFailures, setPollFailures] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true",
  );
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === "/";
  const { addToast } = useToast();
  usePrefetchRoutes();
  const role = localStorage.getItem("appRole") || "admin";

  useEffect(() => {
    setMobileSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
  }, [sidebarCollapsed]);

  // ⌘K / Ctrl+K opens the palette; ⌘B collapses the rail.
  useEffect(() => {
    const onKeyDown = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen((open) => !open);
      } else if (mod && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setSidebarCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Broker auth status + which broker is the default (Settings → Broker). The shell outlives every
  // screen, so a read made once on mount went stale: after a switch in Settings the header still
  // logged in to the OLD broker, and after a login made elsewhere (the 08:30 job, the Settings card)
  // it still offered "Login to Dhan" — a manual Dhan login always generates a NEW token (4 a day).
  // /auth/status is re-read every minute (as the Autotrader banner does), on a "broker-changed"
  // event and whenever a switch refreshes broker.js; /brokers only when the default has changed.
  const defaultRef = useRef(null);
  const refreshBrokerState = useCallback(async (withBrokers = false) => {
    const rowsReq = withBrokers ? fetchBrokers() : null;       // in parallel with the auth read
    rowsReq?.catch(() => {});
    let a = null;
    try {
      a = (await fetchAuthStatus()).data || null;
      setAuth(a || { authenticated: false });
    } catch {
      setAuth((prev) => prev || { authenticated: false });     // a blip keeps the last answer
    }
    let row = defaultRef.current;
    // /auth/status names the CURRENT default: another name means it was switched since the last read
    if (rowsReq || (a?.broker_name && a.broker_name !== row?.name)) {
      try {
        const rows = (await (rowsReq || fetchBrokers())).data?.brokers || [];
        row = rows.find((b) => b.name === a?.broker_name) || rows.find((b) => b.is_default) || null;
        defaultRef.current = row;
        setDefaultBroker(row);
      } catch { /* an older backend without /brokers: keep the last row */ }
    }
    return { auth: a, broker: row };
  }, []);

  useEffect(() => {
    refreshBrokerState(true);
    refreshBrokerInfo();      // primes every "Kite" / "m.Stock" label in the app
  }, [refreshBrokerState]);
  usePolling(() => { refreshBrokerState(); }, 60000, { immediate: false });
  // Any screen that switches, logs in, pastes a token or logs out announces it with
  // window.dispatchEvent(new Event("broker-changed")) (the Settings broker card does); a screen
  // that doesn't is caught up by the minute poll.
  useEffect(() => {
    const onChanged = () => { refreshBrokerState(true); };
    window.addEventListener("broker-changed", onChanged);
    return () => window.removeEventListener("broker-changed", onChanged);
  }, [refreshBrokerState]);
  // A switch in Settings also calls refreshBrokerInfo(): follow broker.js at once, not at the next poll.
  useBrokerName();
  const switchedTo = brokerInfo().name;
  useEffect(() => {
    if (defaultRef.current && defaultRef.current.name !== switchedTo) refreshBrokerState(true);
  }, [switchedTo, refreshBrokerState]);

  // Funds free at the default broker, for the top bar. Only while a broker session exists; the
  // backend shares one broker call between every open panel for 30 s, so a minute here is plenty.
  // Re-read at once when a trade opens or closes — that is when the figure actually moves.
  const openCount = traderStatus?.open_trades?.length || 0;
  const pollFunds = useCallback(() => {
    if (!auth?.authenticated) return;
    fetchBrokerFunds().then((r) => setFunds(r.data)).catch(() => {});
  }, [auth?.authenticated]);
  useEffect(() => {
    if (!auth?.authenticated) { setFunds(null); return; }
    pollFunds();
  }, [auth?.authenticated, auth?.broker, openCount, pollFunds]);
  usePolling(pollFunds, 60000, { enabled: !!auth?.authenticated, immediate: false });

  // Poll auto-trader status (admin and user roles both see the autotrader)
  // together with the both-books position list. Repeated failures mean the
  // backend is unreachable — say so instead of quietly showing stale numbers.
  // Paused while the tab is hidden (usePolling), refreshed on demand after a
  // control in the top bar changes state.
  const refreshStatus = useCallback(async () => {
    const [s, p] = await Promise.allSettled([getAutoTraderStatus(), fetchAutotradePositions()]);
    if (s.status !== "fulfilled") { setPollFailures((n) => n + 1); return; }
    const rows = p.status === "fulfilled" ? p.value?.data?.positions : null;
    setTraderStatus(mergeStatus(s.value.data, Array.isArray(rows) ? rows : null));
    setPollFailures(0);
  }, []);
  usePolling(refreshStatus, 5000);
  // Optimistic patch from a top-bar control (HALT / release), then reconcile.
  const patchStatus = useCallback((patch) => {
    setTraderStatus((s) => (s ? {
      ...s, ...patch,
      autopilot_running: !!(patch.autotrade_enabled ?? s.autotrade_enabled) && !(patch.kill_switch ?? s.kill_switch),
    } : s));
    refreshStatus();
  }, [refreshStatus]);

  // Tab title carries autopilot state + P&L, and the current screen.
  useEffect(() => {
    const active = traderStatus?.autopilot_running;
    const pnl = traderStatus?.today_pnl || 0;
    const pnlStr = `${pnl >= 0 ? "+" : "−"}₹${Math.abs(pnl).toFixed(0)}`;
    const page = findNavItem(location.pathname)?.label;
    document.title = `${active ? "🟢" : "🔴"} ${pnlStr} · ${page || "Infinity"}`;
  }, [traderStatus, location.pathname]);

  const openTradeCount = traderStatus?.open_trades?.length || 0;
  const todayPnl = traderStatus?.today_pnl || 0;
  const isPaper = traderStatus?.is_paper ?? true;
  const brokerLabel = headerBrokerLabel(auth?.broker_name, defaultBroker);

  const handleBrokerLogin = useCallback(async () => {
    // a login step the header can't finish (a pasted token, an OTP) lives on the broker's card
    const toBrokerSettings = () => navigate("/settings?tab=broker");
    try {
      // Re-read first: what the header shows may be minutes old. The click must go to the CURRENT
      // default, and must not replace a session made elsewhere — a manual Dhan login generates a
      // NEW token even while one is live, and the day allows only 4.
      const fresh = await refreshBrokerState(true);
      if (!fresh.auth) {
        addToast("Couldn't check the broker session — is the backend reachable?", "error");
        return;
      }
      const name = fresh.auth.broker_name || fresh.broker?.name || null;
      const target = fresh.broker && (!name || fresh.broker.name === name) ? fresh.broker : null;
      const label = headerBrokerLabel(name, target);
      if (fresh.auth.authenticated) {
        addToast(`Already signed in to ${label}`, "success");
        return;
      }
      // Log in to whichever broker is the default; older backends only know /auth/login-url.
      // /brokers/{name}/login/start is admin-only: a "user" role (or a 403) falls
      // back to the plain Kite login URL, but only while Kite is the default.
      let step = null;
      if (target && role !== "user") {
        try {
          step = (await startBrokerLogin(target.name)).data;
        } catch (e) {
          if (e?.response?.status !== 403) throw e;
        }
      }
      if (step?.kind === "auto") {
        // m.Stock, Dhan: the server signs in with its own keys — no redirect, nothing to type
        addToast(`Signing in to ${label}…`, "info");
        try {
          await autoLoginBroker(target.name);
          addToast(`Signed in to ${label}`, "success");
        } catch (e) {
          // No reply (client timeout, or a proxy gateway error with no detail) is NOT a failed login:
          // the server may still be signing in. Don't send the user off to paste a token meanwhile.
          const status = e?.response?.status;
          const noReply = !e?.response || ([502, 503, 504].includes(status) && !e.response.data?.detail);
          if (noReply) {
            addToast(`No reply yet — the server may still be signing in to ${label}. Check again in a minute.`, "info");
          } else {
            addToast(e?.response?.data?.detail || `Couldn't sign in to ${label} — see Settings → Broker`, "error");
            // a broker that also takes a pasted token (Dhan): open the card with the Paste token button
            if (target.manual_token_login) toBrokerSettings();
          }
        }
        refreshBrokerState();
        return;
      }
      if (step && step.kind !== "redirect") {
        addToast(`Finish the ${label} login in Settings → Broker`, "info");
        toBrokerSettings();
        return;
      }
      if (!step && name && name !== "KITE") {
        // /auth/login-url is Kite's: it would sign in to Kite while another broker is the default
        addToast(`${label} login is in Settings → Broker (admin only)`, "error");
        return;
      }
      const url = step ? step.url : (await fetchLoginUrl()).data?.url;
      if (!url) {
        addToast("Login URL not available — is the backend reachable?", "error");
        return;
      }
      window.location.href = url;
    } catch (err) {
      addToast(err?.response?.data?.detail || "Failed to get login URL", "error");
    }
  }, [refreshBrokerState, addToast, role, navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("appToken");
    localStorage.removeItem("appRole");
    window.dispatchEvent(new Event("app-logout"));
  }, []);

  const allowTrading = ["admin", "user"].includes(role);

  return (
    <TraderStatusContext.Provider value={traderStatus}>
    <div className="min-h-screen text-mid">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        openTradeCount={openTradeCount}
        todayPnl={todayPnl}
        traderActive={!!traderStatus?.autopilot_running}
        traderMode={isPaper ? "PAPER" : "LIVE"}
        role={role}
        onOpenCommand={() => setCommandOpen(true)}
      />

      <div className={`min-h-screen min-w-0 transition-[margin] duration-300 ease-swift ${sidebarCollapsed ? "lg:ml-[76px]" : "lg:ml-64"}`}>
        <TopBar
          onOpenMobileNav={() => setMobileSidebarOpen(true)}
          onOpenCommand={() => setCommandOpen(true)}
          traderStatus={traderStatus}
          auth={auth}
          funds={funds}
          brokerLabel={brokerLabel}
          onBrokerLogin={handleBrokerLogin}
          onLogout={handleLogout}
          onStatusPatch={patchStatus}
          offline={pollFailures >= 3}
        />

        <main className={`app-canvas min-h-[calc(100vh-4rem)] ${isDashboard ? "p-4 sm:p-5 lg:p-6" : "workspace-main p-3 sm:p-5 lg:p-6"}`}>
          <ErrorBoundary routeKey={location.pathname}>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/decide" element={<Decide />} />
                <Route path="/autotrader" element={allowTrading ? <AutoTrader /> : <Navigate to="/" replace />} />
                <Route path="/manual-trader" element={allowTrading ? <ManualTrader /> : <Navigate to="/" replace />} />
                <Route path="/algo-ai" element={allowTrading ? <AlgoAI /> : <Navigate to="/" replace />} />
                <Route path="/rules-lab" element={<RulesLab />} />
                <Route path="/garch-range" element={<GarchRange />} />
                <Route path="/garch-fences" element={<GarchFences />} />
                <Route path="/structure-regime" element={<StructureRegime />} />
                <Route path="/oi-fib" element={<OIFib />} />
                <Route path="/equity-edge" element={<EquityEdge />} />
                <Route path="/ml" element={<MLModels />} />
                <Route path="/backtests" element={<Backtests />} />
                <Route path="/rules" element={<RuleBuilder />} />
                <Route path="/rules/tutorial" element={<RuleTutorial />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/data" element={<DataCollection />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {/* Unknown paths (old bookmarks, typos) land on the dashboard instead of a blank canvas. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} role={role} />
    </div>
    </TraderStatusContext.Provider>
  );
}

export default function App() {
  const [appAuthed, setAppAuthed] = useState(!!localStorage.getItem("appToken"));

  // Forced logout (401 from the axios interceptor)
  useEffect(() => {
    const handler = () => setAppAuthed(false);
    window.addEventListener("app-logout", handler);
    return () => window.removeEventListener("app-logout", handler);
  }, []);

  // Verify stored token on mount
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  useEffect(() => {
    if (!appAuthed || justLoggedIn) {
      setJustLoggedIn(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      appVerify()
        .then((r) => {
          if (r.data?.role) localStorage.setItem("appRole", r.data.role);
        })
        .catch((err) => {
          // Only a definite 401 means the token is bad. A network error or a
          // 5xx while the backend restarts must keep the session — the status
          // poller shows the offline state and the axios interceptor logs out
          // if a later request really is refused.
          if (err?.response?.status !== 401) return;
          localStorage.removeItem("appToken");
          localStorage.removeItem("appRole");
          setAppAuthed(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [appAuthed, justLoggedIn]);

  return (
    <ThemeProvider>
      <div className="aurora" aria-hidden="true"><span /><span /><span /></div>
      {appAuthed ? (
        <ToastProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ConfirmProvider>
        </ToastProvider>
      ) : (
        <Login
          onLogin={() => {
            setJustLoggedIn(true);
            setAppAuthed(true);
          }}
        />
      )}
    </ThemeProvider>
  );
}
